import React from 'react';
import Icon from '../../../components/AppIcon';

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram', icon: 'Instagram', color: '#E1306C' },
  { value: 'tiktok', label: 'TikTok', icon: 'Music', color: '#69C9D0' },
  { value: 'youtube', label: 'YouTube', icon: 'Youtube', color: '#FF0000' },
  { value: 'twitter', label: 'X / Twitter', icon: 'Twitter', color: '#1DA1F2' },
  { value: 'facebook', label: 'Facebook', icon: 'Facebook', color: '#1877F2' },
  { value: 'other', label: 'Other', icon: 'Globe', color: '#94a3b8' },
];

const AUDIENCE_OPTIONS = [
  { value: 'under_1k', label: 'Under 1,000' },
  { value: '1k_10k', label: '1,000 – 10,000' },
  { value: '10k_50k', label: '10,000 – 50,000' },
  { value: '50k_100k', label: '50,000 – 100,000' },
  { value: '100k_500k', label: '100,000 – 500,000' },
  { value: 'over_500k', label: 'Over 500,000' },
];

const EARNINGS_OPTIONS = [
  { value: 'under_500', label: 'Under $500/month' },
  { value: '500_2k', label: '$500 – $2,000/month' },
  { value: '2k_5k', label: '$2,000 – $5,000/month' },
  { value: '5k_10k', label: '$5,000 – $10,000/month' },
  { value: 'over_10k', label: 'Over $10,000/month' },
];

const inputStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  color: '#fff',
  padding: '10px 14px',
  width: '100%',
  fontSize: '14px',
  outline: 'none',
  transition: 'border-color 0.2s',
};

const ScoreBadge = ({ score }) => {
  const tier = score >= 750 ? 'Platinum' : score >= 500 ? 'Gold' : score >= 250 ? 'Silver' : 'Bronze';
  const colors = {
    Platinum: { bg: 'rgba(139,92,246,0.15)', border: 'rgba(139,92,246,0.4)', text: '#a78bfa' },
    Gold: { bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.4)', text: '#fbbf24' },
    Silver: { bg: 'rgba(148,163,184,0.15)', border: 'rgba(148,163,184,0.4)', text: '#94a3b8' },
    Bronze: { bg: 'rgba(180,83,9,0.15)', border: 'rgba(180,83,9,0.4)', text: '#d97706' },
  };
  const c = colors?.[tier];
  return (
    <div
      className="flex items-center gap-3 p-4 rounded-xl"
      style={{ background: c?.bg, border: `1px solid ${c?.border}` }}
    >
      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: c?.bg, border: `2px solid ${c?.border}` }}>
        <Icon name="Star" size={20} color={c?.text} />
      </div>
      <div>
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>Your SocialCents Score</p>
        <p className="text-2xl font-bold" style={{ color: c?.text, fontFamily: 'DM Sans, sans-serif' }}>{score}</p>
        <p className="text-xs font-medium" style={{ color: c?.text }}>{tier} Tier</p>
      </div>
      <div className="ml-auto text-right">
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>Eligibility</p>
        <p className="text-sm font-semibold" style={{ color: score >= 250 ? '#4ade80' : '#f87171' }}>
          {score >= 250 ? 'Eligible' : 'Low Score'}
        </p>
      </div>
    </div>
  );
};

const SocialVerificationStep = ({ data, onChange, errors, scScore }) => {
  const handleChange = (field, value) => onChange({ ...data, [field]: value });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Social & Creator Verification
        </h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
          Confirm your creator profile details for eligibility assessment.
        </p>
      </div>
      {/* SC Score */}
      <ScoreBadge score={scScore || 0} />
      {/* Social Handle */}
      <div>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'rgba(255,255,255,0.7)', marginBottom: '6px' }}>
          Primary Social Handle <span style={{ color: '#f87171' }}>*</span>
        </label>
        <div className="relative">
          <span
            className="absolute left-3 top-1/2 -translate-y-1/2 text-sm"
            style={{ color: 'rgba(255,255,255,0.35)' }}
          >@</span>
          <input
            type="text"
            placeholder="yourusername"
            value={data?.socialHandle || ''}
            onChange={(e) => handleChange('socialHandle', e?.target?.value)}
            style={{ ...inputStyle, paddingLeft: '28px' }}
            onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
            onBlur={(e) => (e.target.style.borderColor = errors?.socialHandle ? '#f87171' : 'rgba(255,255,255,0.1)')}
          />
        </div>
        {errors?.socialHandle && <p style={{ fontSize: '12px', color: '#f87171', marginTop: '4px' }}>{errors?.socialHandle}</p>}
      </div>
      {/* Platform */}
      <div>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'rgba(255,255,255,0.7)', marginBottom: '10px' }}>
          Primary Platform <span style={{ color: '#f87171' }}>*</span>
        </label>
        <div className="grid grid-cols-3 gap-2">
          {PLATFORMS?.map((p) => {
            const isSelected = data?.platform === p?.value;
            return (
              <button
                key={p?.value}
                type="button"
                onClick={() => handleChange('platform', p?.value)}
                className="flex items-center gap-2 px-3 py-2.5 rounded-xl transition-all"
                style={{
                  background: isSelected ? `${p?.color}18` : 'rgba(255,255,255,0.03)',
                  border: `1.5px solid ${isSelected ? p?.color : 'rgba(255,255,255,0.08)'}`,
                  cursor: 'pointer',
                }}
              >
                <Icon name={p?.icon} size={14} color={isSelected ? p?.color : 'rgba(255,255,255,0.4)'} />
                <span style={{ fontSize: '12px', color: isSelected ? p?.color : 'rgba(255,255,255,0.5)', fontWeight: isSelected ? '600' : '400' }}>
                  {p?.label}
                </span>
              </button>
            );
          })}
        </div>
        {errors?.platform && <p style={{ fontSize: '12px', color: '#f87171', marginTop: '6px' }}>{errors?.platform}</p>}
      </div>
      {/* Audience + Earnings */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'rgba(255,255,255,0.7)', marginBottom: '6px' }}>
            Audience Size <span style={{ color: '#f87171' }}>*</span>
          </label>
          <select
            value={data?.audienceSize || ''}
            onChange={(e) => handleChange('audienceSize', e?.target?.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
            onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
            onBlur={(e) => (e.target.style.borderColor = errors?.audienceSize ? '#f87171' : 'rgba(255,255,255,0.1)')}
          >
            <option value="" style={{ background: '#1e293b' }}>Select range</option>
            {AUDIENCE_OPTIONS?.map((o) => (
              <option key={o?.value} value={o?.value} style={{ background: '#1e293b' }}>{o?.label}</option>
            ))}
          </select>
          {errors?.audienceSize && <p style={{ fontSize: '12px', color: '#f87171', marginTop: '4px' }}>{errors?.audienceSize}</p>}
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'rgba(255,255,255,0.7)', marginBottom: '6px' }}>
            Monthly Earnings Range <span style={{ color: '#f87171' }}>*</span>
          </label>
          <select
            value={data?.monthlyEarnings || ''}
            onChange={(e) => handleChange('monthlyEarnings', e?.target?.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
            onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
            onBlur={(e) => (e.target.style.borderColor = errors?.monthlyEarnings ? '#f87171' : 'rgba(255,255,255,0.1)')}
          >
            <option value="" style={{ background: '#1e293b' }}>Select range</option>
            {EARNINGS_OPTIONS?.map((o) => (
              <option key={o?.value} value={o?.value} style={{ background: '#1e293b' }}>{o?.label}</option>
            ))}
          </select>
          {errors?.monthlyEarnings && <p style={{ fontSize: '12px', color: '#f87171', marginTop: '4px' }}>{errors?.monthlyEarnings}</p>}
        </div>
      </div>
    </div>
  );
};

export default SocialVerificationStep;
